import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Col, Row, Nav, NavItem, NavLink } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, generateItemListStructuredData, generateSlug, applySEODate } from "../../utils/seoUtils"
import "./topApps.css"
import PlatformsApi from "../../services/platformsApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function Topapps() {
  const { filterSections } = useFilters()
  const { trackEvent } = useAnalytics()
  const location = useLocation()
  const navigate = useNavigate()
  // Only set when reached via a Weekly Insights "View All" click — landing here
  // directly (sidebar link, bookmark, etc.) leaves this unset, so no back button shows.
  const cameFromWeeklyInsights = location.state?.from === 'weekly-insights'
  const [topSelling, setTopSelling] = useState([])
  const [topEarning, setTopEarning] = useState([])
  const [platforms, setPlatforms] = useState([])
  // Lives in the `?platform=` URL query param (defaulting to Meta) rather than
  // plain state, so it survives a round trip to an app's detail page and back,
  // and so a direct link can pre-select a platform.
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)
  const [itemsToShow, setItemsToShow] = useState(8)
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("selling") // "selling" or "earning"
  const [selectedCardId, setSelectedCardId] = useState(null)

  // locale / country selector states
  const [topCountry, setTopCountry] = useState(() => {
    try {
      return sessionStorage.getItem('topApps_selectedCountry') || 'US'
    } catch (e) {
      return 'US'
    }
  })
  const [topLanguage, setTopLanguage] = useState(() => {
    try {
      return sessionStorage.getItem('topApps_selectedLanguage')
    } catch (e) {
      return null
    }
  })
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [topSellingLastUpdated, setTopSellingLastUpdated] = useState(null)
  const [topEarningLastUpdated, setTopEarningLastUpdated] = useState(null)
  const [topFilterPanelOpen, setTopFilterPanelOpen] = useState(false)
  const [topSellingAppliedFilters, setTopSellingAppliedFilters] = useState({})
  const [topEarningAppliedFilters, setTopEarningAppliedFilters] = useState({})
  const [sortedItems, setSortedItems] = useState([])
  const [isSorted, setIsSorted] = useState(false)
  const [rankSort, setRankSort] = useState(null)

  const isTopSelling = sessionStorage.getItem("topSellingMeta")
  const isTopEarning = sessionStorage.getItem("topEarningMeta")

  useEffect(() => {
    // load countries/locales first, then fetch top lists
    const loadCountries = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      let list = []
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        const res = countriesRes  // keep rest of logic using `res`
        list = res.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)

        const us = list.find(x => x.code === 'US')
        const defaultLocale = us && Array.isArray(us.locales) && us.locales.length > 0 ? us.locales[0] : null
        setTopLanguage(prev => prev || defaultLocale)
      } catch (err) {
        console.error('Error loading countries:', err)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
      } finally {
        setCountriesLoading(false)
        const initCountry = topCountry || (list && list.length > 0 ? list[0].code : 'US')
        const countryEntry = list.find(x => x.code === initCountry)
        const initLangFromList = countryEntry && Array.isArray(countryEntry.locales) && countryEntry.locales.length > 0 ? countryEntry.locales[0] : null
        const initLanguage = topLanguage || initLangFromList || null
        setTopCountry(prev => prev || initCountry)
        setTopLanguage(prev => prev || initLanguage)
        // restore persisted filters for top selling and top earning
        let savedSelling = null
        let savedEarning = null
        try {
          const rs = sessionStorage.getItem('topSellingAppliedFilters')
          const re = sessionStorage.getItem('topEarningAppliedFilters')
          savedSelling = rs ? JSON.parse(rs) : null
          savedEarning = re ? JSON.parse(re) : null
        } catch (e) {
          savedSelling = null
          savedEarning = null
        }
        setTopSellingAppliedFilters(savedSelling || {})
        setTopEarningAppliedFilters(savedEarning || {})

        setLoading(true)
        fetchTopSelling(initCountry, initLanguage, 1, savedSelling || {})
        fetchTopEarning(initCountry, initLanguage, 1, savedEarning || {})
      }
    }

    loadCountries()
  }, [])



  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    fetchTopSelling(topCountry, topLanguage, 1, topSellingAppliedFilters, platformId)
    fetchTopEarning(topCountry, topLanguage, 1, topEarningAppliedFilters, platformId)
  }

  const fetchTopSelling = async (
    country = topCountry,
    language = topLanguage,
    page = 1,
    filters = topSellingAppliedFilters,
    platform = selectedPlatform
  ) => {
    try {
      // Re-arm the skeleton placeholder for every fetch (platform switch included) —
      // not just the initial mount — so the grid doesn't sit stale while new data loads.
      setLoading(true)
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (page) params.page = page

      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.sort_by) params.sort_by = filters.sort_by
        if (filters.sort) params.sort_by = params.sort_by || filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      console.info('fetchTopSelling called', { params })
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/top_selling_this_week`, { params, headers })
      setTopSelling(res.data.games || [])
      try { sessionStorage.setItem("topSellingMeta", JSON.stringify(res.data.games || [])) } catch (e) { /* ignore */ }
      setTopSellingLastUpdated(new Date())
      trackEvent(ANALYTICS_EVENTS.FEATURE.WEEKLY_INSIGHT_VIEWED, {
        insight_type: ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.TOP_SELLING,
        platform,
        country,
      })
    } catch (error) {
      console.error("Error fetching top selling:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTopEarning = async (
    country = topCountry,
    language = topLanguage,
    page = 1,
    filters = topEarningAppliedFilters,
    platform = selectedPlatform
  ) => {
    try {
      // Re-arm the skeleton placeholder for every fetch (platform switch included) —
      // not just the initial mount — so the grid doesn't sit stale while new data loads.
      setLoading(true)
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (page) params.page = page

      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.sort_by) params.sort_by = filters.sort_by
        if (filters.sort) params.sort_by = params.sort_by || filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      console.info('fetchTopEarning called', { params })
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/top_earning`, { params, headers })
      setTopEarning(res.data.games || [])
      try { sessionStorage.setItem("topEarningMeta", JSON.stringify(res.data.games || [])) } catch (e) { /* ignore */ }
      setTopEarningLastUpdated(new Date())
      trackEvent(ANALYTICS_EVENTS.FEATURE.WEEKLY_INSIGHT_VIEWED, {
        insight_type: ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.TOP_EARNING,
        platform,
        country,
      })
    } catch (error) {
      console.error("Error fetching top earning:", error)
    } finally {
      setLoading(false)
    }
  }

  // sorting removed per requirements

  useEffect(() => {
    if (showAll) setItemsToShow(Math.max(topSelling.length, topEarning.length))
    else setItemsToShow(8)
  }, [showAll, topSelling.length, topEarning.length])

  const handleCardClick = (cardId) => {
    setSelectedCardId(cardId)
  }

  const handleTopFilterApply = (filters) => {
    // apply filters to the active tab
    if (activeTab === 'selling') {
      setTopSellingAppliedFilters(filters)
      // persist filters so they survive route changes
      try { sessionStorage.setItem('topSellingAppliedFilters', JSON.stringify(filters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchTopSelling(topCountry, topLanguage, 1, filters)
    } else {
      setTopEarningAppliedFilters(filters)
      try { sessionStorage.setItem('topEarningAppliedFilters', JSON.stringify(filters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchTopEarning(topCountry, topLanguage, 1, filters)
    }
    setTopFilterPanelOpen(false)
  }

  const handleRemoveTopSellingFilter = (key) => {
    const newFilters = { ...(topSellingAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setTopSellingAppliedFilters(newFilters)
      try { sessionStorage.setItem('topSellingAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchTopSelling(topCountry, topLanguage, 1, newFilters)
    }
  }

  const handleRemoveTopEarningFilter = (key) => {
    const newFilters = { ...(topEarningAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setTopEarningAppliedFilters(newFilters)
      try { sessionStorage.setItem('topEarningAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchTopEarning(topCountry, topLanguage, 1, newFilters)
    }
  }

  const displayApps = isSorted ? sortedItems.slice(0, itemsToShow) : (activeTab === 'selling' ? topSelling : topEarning).slice(0, itemsToShow)

  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (app.meta && (app.meta.rank !== undefined)) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const sortByRank = (order = 'asc') => {
    const source = activeTab === 'selling' ? topSelling : topEarning
    const arr = Array.isArray(source) ? [...source] : []
    arr.sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return order === 'asc' ? (ra - rb) : (rb - ra)
    })
    setSortedItems(arr)
    setIsSorted(true)
  }

  const toggleRankSort = () => {
    const next = rankSort === 'asc' ? 'desc' : 'asc'
    setRankSort(next)
    sortByRank(next)
  }

  useEffect(() => {
    if (!rankSort) { setIsSorted(false); setSortedItems([]); return }
    sortByRank(rankSort)
  }, [topSelling, topEarning, activeTab, rankSort])

  // Country/language select rendering (flags, react-select styling) now lives in
  // InsightsPageHeader; this page only needs to re-fetch both tabs' data when the
  // country/language changes.
  const handleTopCountryChange = (code, langForCountry) => {
    setTopCountry(code)
    setTopLanguage(langForCountry)
    setLoading(true)
    // include current applied filters when changing locale
    fetchTopSelling(code, langForCountry, 1, topSellingAppliedFilters)
    fetchTopEarning(code, langForCountry, 1, topEarningAppliedFilters)
  }

  const handleTopLanguageChange = (val) => {
    setTopLanguage(val)
    try {
      sessionStorage.setItem('topApps_selectedLanguage', val || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    // include current applied filters when changing locale
    fetchTopSelling(topCountry, val, 1, topSellingAppliedFilters)
    fetchTopEarning(topCountry, val, 1, topEarningAppliedFilters)
  }

  // Generate structured data for top apps list
  const topAppsStructuredData = generateItemListStructuredData(
    'Top VR Apps 2025',
    displayApps,
    (app) => ({
      '@type': 'SoftwareApplication',
      name: app?.name || 'VR App',
      url: `${window.location.origin}/xr/app/${generateSlug(app?.name || '')}?id=${app?.item_id}`,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Meta Quest',
    })
  );

  // Description/disclaimer reference whichever store is currently selected, rather
  // than always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.topApps.title)}
        description={PAGE_SEO.topApps.description}
        keywords={PAGE_SEO.topApps.keywords}
        canonical={generateCanonicalUrl('/top-apps')}
        robots={PAGE_SEO.topApps.robots}
        structuredData={topAppsStructuredData}
        pageType="topApps"
      />
      <div className="page-content topapps-page">
        {/* Header Bar */}
        <InsightsPageHeader
          onBack={cameFromWeeklyInsights ? () => navigate('/weekly-insights') : undefined}
          title={activeTab === "selling" ? "Top Selling – Weekly Earners" : "Top Earning"}
          description={`Explore the top-grossing apps of the week on the ${platformLabel} Store, ranked by revenue and performance.`}
          disclaimer={`Insights are based on ${platformLabel} Store rankings.`}
          platforms={platforms}
          platformsLoading={platformsLoading}
          selectedPlatform={selectedPlatform}
          onPlatformChange={handlePlatformChange}
          countriesList={countriesList}
          countryOptions={countryOptions}
          countriesLoading={countriesLoading}
          selectedCountry={topCountry}
          selectedLanguage={topLanguage}
          onCountryChange={handleTopCountryChange}
          onLanguageChange={handleTopLanguageChange}
          appliedFilters={activeTab === 'selling' ? topSellingAppliedFilters : topEarningAppliedFilters}
          onRemoveFilter={activeTab === 'selling' ? handleRemoveTopSellingFilter : handleRemoveTopEarningFilter}
          lastUpdated={activeTab === 'selling' ? topSellingLastUpdated : topEarningLastUpdated}
          onOpenFilters={() => setTopFilterPanelOpen(true)}
          rankSort={rankSort}
          onToggleSort={toggleRankSort}
        />


        {/* Tab Navigation */}
        <div className="topapps-tabs">
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === "selling" ? "active" : ""}
                onClick={() => setActiveTab("selling")}
              >
                Top Selling
              </NavLink>
            </NavItem>
            {/* <NavItem>
              <NavLink
                className={activeTab === "earning" ? "active" : ""}
                onClick={() => setActiveTab("earning")}
              >
                Top Earning
              </NavLink>
            </NavItem> */}
          </Nav>
        </div>

        {/* Content Area */}
        <div className="topapps-content">
          {/* App Grid */}
          {loading ? (
            <Row className="g-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Col key={index} xs={12} sm={6}>
                  <div className="topapps-card">
                    <TopAppCardSkeleton />
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <>
              {displayApps.length > 0 ? (
                <Row className="g-3">
                  {displayApps.map((app, index) => (
                    <Col key={app?.item_id || index} xs={12} sm={6}>
                      <div
                        className={`topapps-card ${selectedCardId === app?.item_id ? 'selected' : ''}`}
                        onClick={() => handleCardClick(app?.item_id)}
                      >
                        <TopAppCardM
                          gamedata={app}
                          priceLayout="discounts"
                          showEnds={false}
                          insightType={activeTab === 'selling' ? ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.TOP_SELLING : ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.TOP_EARNING}
                          sourceSurface="top_apps_page"
                          platformIcon={platformIconUrl}
                          platformName={platformLabel}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                    {(activeTab === 'selling' ? Object.keys(topSellingAppliedFilters || {}) : Object.keys(topEarningAppliedFilters || {})).length > 0
                      ? 'No results matched with filter.'
                      : 'No results to display.'}
                  </p>
                </div>
              )}

              {/* See More/Less Button */}
              {!loading && displayApps.length > 0 && (
                <div className="text-center mt-4">
                  {!showAll && Math.max(topSelling.length, topEarning.length) > 4 ? (
                    <Button
                      className="see-more-btn"
                      onClick={() => setShowAll(true)}
                    >
                      See More
                    </Button>
                  ) : showAll ? (
                    <Button
                      className="see-less-btn"
                      onClick={() => setShowAll(false)}
                    >
                      See Less
                    </Button>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <FilterPanel
        open={topFilterPanelOpen}
        sections={filterSections.length > 0 ? filterSections : undefined}
        // Platform is already selectable via the Meta Quest/Steam tabs above -
        // don't duplicate it inside the filter panel.
        hidePlatformSection
        onClose={() => setTopFilterPanelOpen(false)}
        onApply={handleTopFilterApply}
        initialValues={activeTab === 'selling' ? topSellingAppliedFilters : topEarningAppliedFilters}
      />
    </React.Fragment>
  )
}
